import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Smartphone, WifiOff } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api/cliente.js';
import { Carregando, ErroSeguro } from '../componentes/estados.js';
import { ModalAcao } from '../componentes/ModalAcao.js';
import { SeletorAutomacao } from '../componentes/SeletorAutomacao.js';
import { useSessao } from '../sessao.js';

/**
 * Conexão do WhatsApp.
 *
 * O QR rotaciona a cada ~45 segundos, então a tela o recarrega sozinha a cada
 * 20s enquanto aguarda leitura. Foi o problema que derrubou a validação manual
 * da Fase 3: QR estático não sobrevive ao tempo entre gerar e escanear.
 *
 * O código de pareamento aparece como alternativa, porque digitar não tem
 * corrida contra o relógio.
 */
export function Conexao() {
  const cliente = useQueryClient();
  const { ehAdmin } = useSessao();
  const [numero, setNumero] = useState('');
  const [desconectando, setDesconectando] = useState(false);

  const {
    data: conexao,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['conexao'],
    queryFn: () => api.conexao(),
  });

  const { data: status } = useQuery({
    queryKey: ['conexao-status'],
    queryFn: () => api.statusConexao(),
    refetchInterval: 5_000,
  });

  const conectado = status?.status === 'conectado';

  /*
   * Sessão caída de um jeito que pedir QR não resolve.
   *
   * Quando o aparelho revoga o dispositivo, a instância continua existindo na
   * Evolution e se dizendo `open`. Pedir QR nesse estado devolve um código que
   * nunca vai valer, e a tela gira para sempre — foi assim que a conexão de uma
   * usuária ficou sete dias parecendo saudável. Aqui o caminho é refazer.
   */
  const precisaRecriar = status?.precisaRecriar === true;
  const aguardandoLeitura = conexao?.instancia != null && !conectado && !precisaRecriar;

  const {
    data: qr,
    isError: erroQr,
    error: detalheErroQr,
  } = useQuery({
    queryKey: ['conexao-qrcode'],
    queryFn: () => api.qrcode(),
    // Só busca QR quando há instância e ela ainda não conectou. Recarrega antes
    // dos ~45s de validade para a tela nunca mostrar um código morto.
    enabled: aguardandoLeitura,
    refetchInterval: 20_000,
  });

  const criar = useMutation({
    mutationFn: (num?: string) => api.criarConexao(num),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['conexao'] });
      await cliente.invalidateQueries({ queryKey: ['conexao-qrcode'] });
    },
  });

  /**
   * Apaga a conexão dos dois lados para poder recriar.
   *
   * Necessário quando a instância some da Evolution e continua no nosso banco —
   * estado em que pedir QR devolve 404 para sempre.
   */
  const refazer = useMutation({
    mutationFn: () => api.refazerConexao(),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['conexao'] });
      await cliente.invalidateQueries({ queryKey: ['conexao-qrcode'] });
      await cliente.invalidateQueries({ queryKey: ['conexao-status'] });
    },
  });

  if (isLoading) return <Carregando />;
  if (isError) return <ErroSeguro aoTentarNovamente={() => void refetch()} />;

  return (
    <div className="max-w-[640px]">
      <header className="mb-4">
        <h1 className="text-[length:var(--text-tela)] font-semibold text-texto">
          Conexão do WhatsApp
        </h1>
        <p className="mt-0.5 text-[length:var(--text-secundario)] text-texto-secundario">
          Conecte seu WhatsApp para que tudo que você mandar na conversa consigo mesmo vire
          lançamento.
        </p>
      </header>

      <section
        className={`mb-4 flex items-center gap-3 rounded-[var(--radius-card)] border p-4 shadow-[var(--shadow-card)] ${
          conectado ? 'border-entrada-borda bg-entrada-bg' : 'border-saida-borda bg-saida-bg'
        }`}
      >
        <span
          className={`flex h-9 w-9 items-center justify-center rounded-[var(--radius-controle)] ${
            conectado ? 'bg-entrada text-white' : 'bg-saida text-white'
          }`}
        >
          {conectado ? <CheckCircle2 size={18} /> : <WifiOff size={18} />}
        </span>
        <div className="flex-1">
          <p className={`font-semibold ${conectado ? 'text-entrada-fg' : 'text-saida-fg'}`}>
            {conectado ? 'CONECTADO' : 'DESCONECTADO'}
          </p>
          <p className="text-[length:var(--text-secundario)] text-texto-secundario">
            {conectado ? (
              <>
                Número <span className="mono">{status?.telefone ?? '—'}</span>
                {status?.ultimaMensagemEm && (
                  <>
                    {' '}
                    · última mensagem em{' '}
                    <span className="mono">
                      {new Date(status.ultimaMensagemEm).toLocaleString('pt-BR')}
                    </span>
                  </>
                )}
              </>
            ) : (
              'Seus lançamentos não estão sendo recebidos.'
            )}
          </p>
        </div>
      </section>

      {/* Sempre visível, mesmo desconectado. Escondê-lo até a conexão existir
          parecia lógico, mas na prática deixa a configuração invisível
          justamente para quem está montando o sistema pela primeira vez. */}
      <SeletorAutomacao />

      {precisaRecriar && ehAdmin && (
        <section className="mb-4 rounded-[var(--radius-card)] border border-saida-borda bg-superficie p-5 shadow-[var(--shadow-card)]">
          <h2 className="font-semibold text-texto">A sessão do WhatsApp caiu</h2>
          <p className="mt-1 text-[length:var(--text-secundario)] text-texto-secundario">
            {status?.erro ??
              'O aparelho não está mais vinculado. Refaça a conexão para voltar a lançar.'}
          </p>
          <button
            type="button"
            onClick={() => refazer.mutate()}
            disabled={refazer.isPending}
            className="mt-4 h-11 rounded-[var(--radius-controle)] bg-primaria px-4 font-medium text-white hover:bg-primaria-hover disabled:opacity-45"
          >
            {refazer.isPending ? 'Refazendo...' : 'Refazer conexão'}
          </button>
        </section>
      )}

      {!conexao?.instancia && ehAdmin && (
        <section className="rounded-[var(--radius-card)] border border-borda bg-superficie p-5 shadow-[var(--shadow-card)]">
          <h2 className="font-semibold text-texto">Conectar um número</h2>
          <p className="mt-1 text-[length:var(--text-secundario)] text-texto-secundario">
            Informe o número com DDI e DDD para receber um código digitável. Deixe em branco para
            conectar lendo o QR Code.
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            <input
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
              placeholder="55 62 99988-1234"
              inputMode="tel"
              className="mono h-11 flex-1 min-w-[180px] rounded-[var(--radius-controle)] border border-borda-input bg-superficie px-3 text-[length:var(--text-corpo)] text-texto placeholder:text-[var(--cor-placeholder)]"
            />
            <button
              type="button"
              onClick={() => criar.mutate(numero.replace(/\D/g, '') || undefined)}
              disabled={criar.isPending}
              className="h-11 rounded-[var(--radius-controle)] bg-primaria px-4 font-medium text-white hover:bg-primaria-hover disabled:opacity-45"
            >
              {criar.isPending ? 'Conectando...' : 'Conectar'}
            </button>
          </div>

          {criar.isError && (
            <p className="mt-3 rounded-[var(--radius-controle)] bg-saida-bg px-3 py-2 text-[length:var(--text-secundario)] text-saida-fg">
              {criar.error instanceof Error ? criar.error.message : 'Não foi possível conectar.'}
            </p>
          )}
        </section>
      )}

      {aguardandoLeitura && (
        <section className="rounded-[var(--radius-card)] border border-borda bg-superficie p-5 text-center shadow-[var(--shadow-card)]">
          {(criar.data?.codigoPareamento ?? qr?.codigoPareamento) ? (
            <>
              <h2 className="font-semibold text-texto">Digite este código no WhatsApp</h2>
              <p className="mono my-4 text-[28px] font-semibold tracking-[0.15em] text-texto">
                {formatarCodigo(criar.data?.codigoPareamento ?? qr?.codigoPareamento ?? '')}
              </p>
              <p className="text-[length:var(--text-secundario)] text-texto-secundario">
                WhatsApp → Aparelhos conectados → Conectar aparelho → Conectar com número de
                telefone
              </p>
            </>
          ) : (criar.data?.qrcode ?? qr?.qrcode) ? (
            <>
              <h2 className="font-semibold text-texto">Escaneie o QR Code</h2>
              <img
                src={criar.data?.qrcode ?? qr?.qrcode ?? ''}
                alt="QR Code para conectar o WhatsApp"
                className="mx-auto my-4 h-[240px] w-[240px] rounded-[var(--radius-controle)] bg-white p-2"
              />
              <p className="text-[length:var(--text-secundario)] text-texto-secundario">
                WhatsApp → Aparelhos conectados → Conectar aparelho.
                <br />O código se renova sozinho a cada 20 segundos.
              </p>
            </>
          ) : erroQr ? (
            /*
             * Antes isto caía no spinner e girava para sempre. Uma tela que gira
             * sem fim é pior que uma que erra: o usuário fica esperando algo que
             * nunca vem, sem saber que precisa agir.
             */
            <>
              <h2 className="font-semibold text-texto">Não foi possível gerar o código</h2>
              <p className="mt-2 text-[length:var(--text-secundario)] text-texto-secundario">
                {detalheErroQr instanceof Error
                  ? detalheErroQr.message
                  : 'A conexão pode ter sido removida do servidor de WhatsApp.'}
              </p>
              <button
                type="button"
                onClick={() => refazer.mutate()}
                disabled={refazer.isPending}
                className="mt-4 rounded-[var(--radius-controle)] bg-primaria px-4 py-2 font-medium text-white hover:bg-primaria-hover disabled:opacity-45"
              >
                {refazer.isPending ? 'Refazendo...' : 'Refazer conexão'}
              </button>
            </>
          ) : (
            <Carregando texto="Gerando código de conexão..." />
          )}
        </section>
      )}

      {conectado && ehAdmin && (
        <button
          type="button"
          onClick={() => setDesconectando(true)}
          className="mt-4 inline-flex items-center gap-1.5 rounded-[var(--radius-controle)] border border-saida-borda px-4 py-2 text-[length:var(--text-secundario)] font-medium text-saida hover:bg-saida-bg"
        >
          <Smartphone size={14} aria-hidden />
          Desconectar...
        </button>
      )}

      <ModalAcao
        aberto={desconectando}
        titulo="Desconectar o WhatsApp"
        variante="destrutiva"
        rotuloConfirmar="Desconectar"
        impacto={
          <p>
            A partir da desconexão, nada que você mandar no WhatsApp será registrado. Os lançamentos
            já existentes permanecem. Para voltar, será preciso conectar o aparelho de novo.
          </p>
        }
        aoConfirmar={async (motivo) => {
          await api.desconectar(motivo);
          await cliente.invalidateQueries({ queryKey: ['conexao'] });
          await cliente.invalidateQueries({ queryKey: ['conexao-status'] });
        }}
        aoFechar={() => setDesconectando(false)}
      />
    </div>
  );
}

/** `D16S3HR1` → `D16S-3HR1`, mais fácil de ler e digitar. */
function formatarCodigo(codigo: string): string {
  return codigo.length === 8 ? `${codigo.slice(0, 4)}-${codigo.slice(4)}` : codigo;
}
